import { memo, useCallback } from "react";

import { useAuthData } from "../auth/auth-data-context";
import { useAuthToken } from "../auth/auth-token-context";
import * as css from "./impersonation-banner.module.css";

/**
 * Renders only when the active session is an admin impersonation. Loud
 * amber treatment so the operator can't miss it. "Exit impersonation"
 * clears the in-memory token and either restores the partner's own
 * persistent session (if one was logged in) or returns to /login.
 */
export const ImpersonationBanner = memo(function ImpersonationBanner() {
  const { actor, isImpersonating, exitImpersonation } = useAuthToken();
  const { partner } = useAuthData();

  const handleExit = useCallback(() => {
    exitImpersonation();
    // Closing the tab is the cleanest UX since the admin opened this
    // window via target="_blank". Falls back to soft navigation if the
    // browser refuses to close (e.g. tab wasn't opened by script).
    window.close();
  }, [exitImpersonation]);

  if (!isImpersonating || !actor) {
    return null;
  }

  return (
    <div className={css.banner} data-testid="impersonation-banner" role="alert">
      <div className={css.message}>
        <span className={css.title}>
          Viewing as {partner?.name ?? "partner"}
        </span>
        <span className={css.detail}>
          Admin impersonation · {actor.adminEmail}
        </span>
      </div>
      <button className={css.exitButton} onClick={handleExit} type="button">
        Exit impersonation
      </button>
    </div>
  );
});
